/**
 * ToolForm Component
 * Side-panel form for editing MCP Actions (Tools).
 * Mirrors AgentForm's slide-in-from-right pattern.
 */

import * as React from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Bell,
  Check,
  Copy,
  Filter,
  Globe,
  Hash,
  MessageCircle,
  Phone,
  Plus,
  Search,
  Settings,
  Shield,
  Star,
  Trash2,
  User,
  Users,
  Zap,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { SelectorVisual } from '@/components/ui/selector-visual'
import { useIsMobile } from '@/hooks/use-mobile'
import { SecretSelectField } from '@/components/tools/SecretSelectField'
import {
  buildWebhookSchemasFromStudioDrafts,
  createHeaderDraft,
  createParamDraft,
  ensurePathPlaceholderInUrl,
  getWebhookHeaderValidationError,
  getWebhookParamValidationError,
  methodUsesQueryParams,
  mapKnownWebhookBackendValidationError,
  normalizeHeaderDrafts,
  normalizeWebhookMethod,
  parseStudioDraftsFromWebhookSchemas,
  reconcilePathParamsWithUrl,
  removePathPlaceholderFromUrl,
  renamePathPlaceholderInUrl,
  sanitizeEnumValues,
  toRequestHeadersPayload,
  type HeaderDraft,
  type ParamDraft,
} from '@/lib/tool-http-schema'
import { formatDrfValidationDetails } from '@/lib/format-drf-validation-details'
import { buildExternalMcpConfig } from '@/lib/tool-mcp-payload'
import { normalizeToolJsonPayload, validateToolJsonPayload } from '@/lib/tool-json-validation'
import { loadToolIconsMap, saveToolIcon } from '@/lib/tool-icons'
import { toolsService } from '@/services/toolsService'
import type { MCPAction, MCPActionCreate, ToolVisibility } from '@/types/tool'
import { TagBadge } from '@/components/ui/tag-badge'

const ICON_OPTIONS = ['filter', 'phone', 'globe', 'star', 'zap', 'bell', 'message', 'user', 'users', 'hash', 'check', 'search', 'settings', 'shield'] as const
const REDACTED_SECRET_VALUE = '••••••••'

function renderVisualIcon(icon: string, colorClass?: string, className?: string) {
  if (icon.startsWith('emoji:')) {
    return <span className={cn('leading-none', className)}>{icon.replace('emoji:', '')}</span>
  }
  const map = {
    filter: Filter,
    phone: Phone,
    globe: Globe,
    star: Star,
    zap: Zap,
    bell: Bell,
    message: MessageCircle,
    user: User,
    users: Users,
    hash: Hash,
    check: Check,
    search: Search,
    settings: Settings,
    shield: Shield,
  } as const
  const IconComponent = map[icon as keyof typeof map] ?? Filter
  return <IconComponent className={cn(className, colorClass)} />
}

interface ToolFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tool?: MCPAction | null
  onSuccess?: () => void
}

type ToolFormState = {
  name: string
  display_name: string
  description: string
  execution_type: 'webhook' | 'mcp_server'
  is_enabled: boolean
  http_method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  http_url: string
  request_headers: HeaderDraft[]
  path_params_schema: ParamDraft[]
  query_params_schema: ParamDraft[]
  mcp_url: string
  mcp_remote_tool_name: string
  mcp_enabled_remote_tools: string[]
  mcp_transport: 'http_jsonrpc' | 'streamable_http'
  mcp_auth_type: 'none' | 'bearer' | 'header'
  mcp_auth_header_name: string
  mcp_auth_secret_id: string
  execution_class: string
  timeout_seconds: number
  retry_count: number
  classification: ToolVisibility
}

type DiscoveredMCPTool = {
  remote_tool_name: string
  display_name: string
  description?: string
  input_schema?: Record<string, unknown>
}

function slugifyToolName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function isValidHttpUrl(value: string): boolean {
  const candidate = value.trim()
  if (!candidate) return false
  try {
    const parsed = new URL(candidate)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

function normalizeStudioVisibility(visibility: ToolVisibility | undefined): Exclude<ToolVisibility, 'mixed'> {
  return visibility === 'internal' || visibility === 'mixed' ? 'internal' : 'external'
}

function toErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'body' in error) {
    const body = (error as { body?: unknown }).body
    if (body && typeof body === 'object') {
      const structuredError = (body as { error?: unknown }).error
      if (structuredError && typeof structuredError === 'object') {
        const message = (structuredError as { message?: unknown }).message
        const details = (structuredError as { details?: unknown }).details
        const detailStr =
          details !== undefined && details !== null && typeof details === 'object'
            ? formatDrfValidationDetails(details)
            : ''
        const knownDetailMessage = detailStr ? mapKnownWebhookBackendValidationError(detailStr) : null
        if (knownDetailMessage) {
          return knownDetailMessage
        }
        if (typeof message === 'string' && message.trim().length > 0) {
          return detailStr.trim().length > 0 ? `${message} (${detailStr})` : message
        }
        if (detailStr.trim().length > 0) {
          return detailStr
        }
      }
      const handshakeError = (body as { handshake?: { error?: unknown } }).handshake?.error
      if (typeof handshakeError === 'string' && handshakeError.trim().length > 0) {
        return handshakeError
      }
      const bodyError = (body as { error?: unknown }).error
      if (typeof bodyError === 'string' && bodyError.trim().length > 0) {
        const knownBodyError = mapKnownWebhookBackendValidationError(bodyError)
        if (knownBodyError) return knownBodyError
        return bodyError
      }
      const entries = Object.entries(body as Record<string, unknown>)
      if (entries.length > 0) {
        const [field, value] = entries[0]
        const detailStr = formatDrfValidationDetails(value)
        if (detailStr.trim().length > 0) {
          return `${field}: ${detailStr}`
        }
      }
    }
  }
  if (error instanceof Error && error.message) return error.message
  return 'Ha ocurrido un error inesperado.'
}

function buildFormState(tool: MCPAction | null | undefined): ToolFormState {
  const normalizedExecutionType = tool?.execution_type === 'mcp_server' ? 'mcp_server' : 'webhook'
  const externalMcpCfg = tool?.http_config?.external_mcp
  const webhookMethod = normalizeWebhookMethod(tool?.http_config?.method)
  const studioDrafts = parseStudioDraftsFromWebhookSchemas(
    webhookMethod,
    tool?.http_config?.path_params_schema,
    tool?.http_config?.query_params_schema,
    tool?.http_config?.request_body_schema,
  )
  return {
    name: tool?.name || '',
    display_name: tool?.display_name || '',
    description: tool?.description || '',
    execution_type: normalizedExecutionType,
    is_enabled: tool?.is_enabled ?? true,
    http_method: webhookMethod,
    http_url: tool?.http_config?.url || '',
    request_headers: normalizeHeaderDrafts(tool?.http_config),
    path_params_schema: studioDrafts.path_params_schema,
    query_params_schema: studioDrafts.query_params_schema,
    mcp_url: tool?.execution_type === 'mcp_server' ? tool?.http_config?.url || '' : '',
    mcp_remote_tool_name: tool?.execution_type === 'mcp_server' ? externalMcpCfg?.remote_tool_name || externalMcpCfg?.tool_id || tool?.name || '' : '',
    mcp_enabled_remote_tools: tool?.execution_type === 'mcp_server'
      ? (
          Array.isArray(externalMcpCfg?.enabled_remote_tools)
            ? externalMcpCfg?.enabled_remote_tools.filter((item): item is string => typeof item === 'string').map((item) => item.trim()).filter(Boolean)
            : (externalMcpCfg?.remote_tool_name ? [externalMcpCfg.remote_tool_name] : [])
        )
      : [],
    mcp_transport: tool?.execution_type === 'mcp_server' ? (externalMcpCfg?.transport || 'http_jsonrpc') : 'http_jsonrpc',
    mcp_auth_type: tool?.execution_type === 'mcp_server' ? (externalMcpCfg?.auth_type || 'none') : 'none',
    mcp_auth_header_name: tool?.execution_type === 'mcp_server' ? (externalMcpCfg?.auth_header_name || 'X-API-Key') : 'X-API-Key',
    mcp_auth_secret_id: tool?.execution_type === 'mcp_server' ? (externalMcpCfg?.auth_secret_id || '') : '',
    execution_class: tool?.execution_class || '',
    timeout_seconds: tool?.timeout_seconds ?? 30,
    retry_count: tool?.retry_count ?? 0,
    classification: normalizeStudioVisibility(tool?.classification),
  }
}

export function ToolForm({ open, onOpenChange, tool, onSuccess }: ToolFormProps) {
  const isMobile = useIsMobile()
  const [loading, setLoading] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [formData, setFormData] = React.useState<ToolFormState>(buildFormState(tool))
  const [editorMode, setEditorMode] = React.useState<'form' | 'json'>('form')
  const [jsonDraft, setJsonDraft] = React.useState('')
  const [jsonError, setJsonError] = React.useState<string | null>(null)
  const [showSchemaJson, setShowSchemaJson] = React.useState(false)
  const [selectedIcon, setSelectedIcon] = React.useState('emoji:🔧')
  const [iconColorClass, setIconColorClass] = React.useState('')
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const [schemaCopied, setSchemaCopied] = React.useState(false)
  const [mcpDiscovering, setMcpDiscovering] = React.useState(false)
  const [mcpExploreError, setMcpExploreError] = React.useState<string | null>(null)
  const [mcpDiscoveredTools, setMcpDiscoveredTools] = React.useState<DiscoveredMCPTool[]>([])
  const [mcpToolDetail, setMcpToolDetail] = React.useState<DiscoveredMCPTool | null>(null)
  const [paramEnumInputByKey, setParamEnumInputByKey] = React.useState<Record<string, string>>({})
  const paramInputRefs = React.useRef<Record<string, HTMLInputElement | null>>({})
  const pendingParamFocusRef = React.useRef<{ refKey: string; caret: number | null } | null>(null)

  const buildParamStableKey = React.useCallback(
    (kind: 'path_params_schema' | 'query_params_schema', param: ParamDraft, paramIndex: number) => {
      const stableParamKey = param.draft_id || `${kind}-${paramIndex}`
      return `${kind}:${stableParamKey}`
    },
    []
  )

  const buildParamInputRefKey = React.useCallback(
    (
      kind: 'path_params_schema' | 'query_params_schema',
      param: ParamDraft,
      paramIndex: number,
      field: 'id' | 'description' | 'value'
    ) => `${buildParamStableKey(kind, param, paramIndex)}:${field}`,
    [buildParamStableKey]
  )

  const setParamInputNode = React.useCallback((refKey: string, node: HTMLInputElement | null) => {
    if (node) {
      paramInputRefs.current[refKey] = node
      return
    }
    delete paramInputRefs.current[refKey]
  }, [])

  const rememberParamInputFocus = React.useCallback((refKey: string, target: HTMLInputElement) => {
    pendingParamFocusRef.current = {
      refKey,
      caret: target.selectionStart,
    }
  }, [])

  React.useLayoutEffect(() => {
    const pending = pendingParamFocusRef.current
    if (!pending) return
    pendingParamFocusRef.current = null

    const target = paramInputRefs.current[pending.refKey]
    if (!target || document.activeElement === target) return

    target.focus({ preventScroll: true })
    if (pending.caret !== null) {
      const caret = Math.min(Math.max(pending.caret, 0), target.value.length)
      target.setSelectionRange(caret, caret)
    }
  }, [formData.path_params_schema, formData.query_params_schema])

  React.useEffect(() => {
    if (!open) return
    setErrorMessage(null)
    setSchemaCopied(false)
    setFormData(buildFormState(tool))
    setEditorMode('form')
    setJsonDraft('')
    setJsonError(null)
    setShowSchemaJson(false)
    const saved = loadToolIconsMap()[tool?.id || '']
    if (saved?.icon) {
      setSelectedIcon(saved.icon)
      setIconColorClass(saved.colorClass || '')
    } else {
      setSelectedIcon('emoji:🔧')
      setIconColorClass('')
    }
    setMcpDiscovering(false)
    setMcpExploreError(null)
    const externalMcp = tool?.http_config?.external_mcp
    const catalog = Array.isArray(externalMcp?.remote_tools_catalog)
      ? externalMcp.remote_tools_catalog
      : []
    const enabled = Array.isArray(externalMcp?.enabled_remote_tools)
      ? externalMcp.enabled_remote_tools.filter((item): item is string => typeof item === 'string')
      : []
    setMcpDiscoveredTools(
      catalog.map((item) => ({
        remote_tool_name: String(item.remote_tool_name || ''),
        display_name: String(item.display_name || item.remote_tool_name || ''),
        description: item.description,
        input_schema: item.input_schema,
      })).filter((item) => item.remote_tool_name.length > 0)
    )
    if (enabled.length > 0) {
      setFormData((prev) => ({ ...prev, mcp_enabled_remote_tools: enabled }))
    }
    setMcpToolDetail(null)
    setParamEnumInputByKey({})
  }, [tool, open])

  React.useEffect(() => {
    if (formData.execution_type !== 'mcp_server') return
    const nextName = slugifyToolName(formData.display_name)
    if (formData.name === nextName) return
    setFormData((prev) => ({ ...prev, name: nextName }))
  }, [formData.execution_type, formData.display_name, formData.name])

  const discoverMcpTools = async () => {
    if (!formData.mcp_url.trim()) {
      setMcpExploreError('La URL del servidor MCP es obligatoria.')
      return
    }
    try {
      setMcpDiscovering(true)
      setMcpExploreError(null)
      const response = await toolsService.externalExplore({
        server_url: formData.mcp_url.trim(),
        transport: formData.mcp_transport,
        auth_type: formData.mcp_auth_type,
        auth_header_name: formData.mcp_auth_header_name || 'X-API-Key',
        auth_secret_id: formData.mcp_auth_secret_id.trim() || undefined,
        timeout_seconds: formData.timeout_seconds,
      })
      const tools = (response.tools || []).map((item) => ({
        remote_tool_name: String(item.remote_tool_name || ''),
        display_name: String(item.display_name || item.remote_tool_name || ''),
        description: item.description,
        input_schema: item.input_schema,
      })).filter((item) => item.remote_tool_name.length > 0)
      setMcpDiscoveredTools(tools)
      setFormData((prev) => {
        const previousEnabled = new Set(prev.mcp_enabled_remote_tools)
        const previousKnown = new Set(mcpDiscoveredTools.map((tool) => tool.remote_tool_name))
        const nextEnabled = tools
          .map((tool) => tool.remote_tool_name)
          .filter((remoteName) => previousEnabled.has(remoteName) || !previousKnown.has(remoteName))
        return { ...prev, mcp_enabled_remote_tools: nextEnabled }
      })
      if (tools.length === 0) {
        setMcpExploreError('El servidor respondió, pero no expone herramientas en tools/list.')
      }
    } catch (error) {
      setMcpExploreError(toErrorMessage(error))
      setMcpDiscoveredTools([])
    } finally {
      setMcpDiscovering(false)
    }
  }

  const toggleMcpRemoteTool = (remoteToolName: string, enabled: boolean) => {
    setFormData((prev) => {
      const current = new Set(prev.mcp_enabled_remote_tools)
      if (enabled) current.add(remoteToolName)
      else current.delete(remoteToolName)
      return { ...prev, mcp_enabled_remote_tools: Array.from(current) }
    })
  }

  const buildJsonSnapshot = React.useCallback(() => {
    const payload: Record<string, unknown> = {
      execution_type: formData.execution_type,
      name: formData.name,
      display_name: formData.display_name,
      description: formData.description,
      is_enabled: formData.is_enabled,
      timeout_seconds: formData.timeout_seconds,
      retry_count: formData.retry_count,
      classification: formData.classification,
    }

    if (formData.execution_type === 'webhook') {
      const schemaPayload = buildWebhookSchemasFromStudioDrafts(
        formData.http_method,
        formData.path_params_schema,
        formData.query_params_schema,
      )
      payload.http_config = {
        method: formData.http_method,
        url: formData.http_url,
        request_headers: toRequestHeadersPayload(formData.request_headers),
        ...schemaPayload,
      }
    } else {
      payload.http_config = {
        method: 'POST',
        url: formData.mcp_url,
        external_mcp: {
          server_url: formData.mcp_url,
          transport: formData.mcp_transport,
          auth_type: formData.mcp_auth_type,
          auth_header_name: formData.mcp_auth_header_name,
          auth_secret_id: formData.mcp_auth_secret_id,
          enabled_remote_tools: formData.mcp_enabled_remote_tools,
          remote_tools_catalog: mcpDiscoveredTools.map((item) => ({
            remote_tool_name: item.remote_tool_name,
            display_name: item.display_name || item.remote_tool_name,
            description: item.description || '',
            input_schema: item.input_schema || {},
          })),
        },
      }
      payload.mcp_url = formData.mcp_url
      payload.mcp_transport = formData.mcp_transport
      payload.mcp_auth_type = formData.mcp_auth_type
      payload.mcp_auth_header_name = formData.mcp_auth_header_name
      payload.mcp_auth_secret_id = formData.mcp_auth_secret_id
      payload.mcp_enabled_remote_tools = formData.mcp_enabled_remote_tools
    }

    return JSON.stringify(payload, null, 2)
  }, [formData, mcpDiscoveredTools])

  const buildSchemaExportSnapshot = React.useCallback(() => {
    const schemaPayload = buildWebhookSchemasFromStudioDrafts(
      formData.http_method,
      formData.path_params_schema,
      formData.query_params_schema
    )
    const redactedHeaders = toRequestHeadersPayload(formData.request_headers).map((header) => {
      if (header.value_type === 'secreto') {
        return {
          ...header,
          secret_id: REDACTED_SECRET_VALUE,
        }
      }
      return header
    })
    return JSON.stringify(
      {
        method: formData.http_method,
        url: formData.http_url,
        request_headers: redactedHeaders,
        ...schemaPayload,
      },
      null,
      2
    )
  }, [formData])

  const handleCopySchema = async () => {
    try {
      await navigator.clipboard.writeText(buildSchemaExportSnapshot())
      setSchemaCopied(true)
      setTimeout(() => setSchemaCopied(false), 1500)
    } catch {
      setSchemaCopied(false)
    }
  }

  const applyJsonDraft = (options?: { suppressErrors?: boolean }): ToolFormState | null => {
    const suppressErrors = options?.suppressErrors ?? false
    try {
      const parsed = JSON.parse(jsonDraft) as Record<string, unknown>
      if (!parsed || typeof parsed !== 'object') {
        if (!suppressErrors) setJsonError('El contenido JSON debe ser un objeto.')
        return null
      }

      const validationErrors = validateToolJsonPayload(parsed)
      if (validationErrors.length > 0) {
        if (!suppressErrors) setJsonError(validationErrors.join('\n'))
        return null
      }

      const normalizedParsed = normalizeToolJsonPayload(parsed)
      const parsedHttpConfigRaw = (normalizedParsed as { http_config?: unknown }).http_config
      const parsedHttpConfig =
        parsedHttpConfigRaw && typeof parsedHttpConfigRaw === 'object'
          ? (parsedHttpConfigRaw as Record<string, unknown>)
          : null
      const parsedExternalMcpRaw = parsedHttpConfig?.external_mcp
      const parsedExternalMcp =
        parsedExternalMcpRaw && typeof parsedExternalMcpRaw === 'object'
          ? (parsedExternalMcpRaw as Record<string, unknown>)
          : null

      const coerceMethod = (value: unknown): ToolFormState['http_method'] | undefined => {
        if (typeof value !== 'string') return undefined
        const upper = value.toUpperCase()
        if (upper === 'GET' || upper === 'POST' || upper === 'PUT' || upper === 'PATCH' || upper === 'DELETE') {
          return upper
        }
        return undefined
      }

      const parsedHttpMethod =
        coerceMethod((normalizedParsed as { http_method?: unknown }).http_method)
        ?? coerceMethod(parsedHttpConfig?.method)
      const parsedHttpUrl =
        typeof (normalizedParsed as { http_url?: unknown }).http_url === 'string'
          ? String((normalizedParsed as { http_url?: string }).http_url || '')
          : (typeof parsedHttpConfig?.url === 'string' ? parsedHttpConfig.url : undefined)
      const parsedMcpUrl =
        typeof (normalizedParsed as { mcp_url?: unknown }).mcp_url === 'string'
          ? String((normalizedParsed as { mcp_url?: string }).mcp_url || '')
          : (typeof parsedExternalMcp?.server_url === 'string'
              ? parsedExternalMcp.server_url
              : (typeof parsedHttpConfig?.url === 'string' ? parsedHttpConfig.url : undefined))

      const parsedRequestHeaders =
        (normalizedParsed as { request_headers?: unknown }).request_headers
        ?? parsedHttpConfig?.request_headers
      const parsedPathParams =
        (normalizedParsed as { path_params_schema?: unknown }).path_params_schema
        ?? parsedHttpConfig?.path_params_schema
      const parsedQueryParams =
        (normalizedParsed as { query_params_schema?: unknown }).query_params_schema
        ?? parsedHttpConfig?.query_params_schema
      const parsedRequestBodyParams =
        (normalizedParsed as { request_body_schema?: unknown }).request_body_schema
        ?? parsedHttpConfig?.request_body_schema
      const parsedMethod =
        parsedHttpMethod ?? formData.http_method
      const parsedStudioDrafts = parseStudioDraftsFromWebhookSchemas(
        parsedMethod,
        parsedPathParams,
        parsedQueryParams,
        parsedRequestBodyParams,
      )

      const parsedEnabledRemoteToolsRoot = (normalizedParsed as { mcp_enabled_remote_tools?: unknown }).mcp_enabled_remote_tools
      const parsedEnabledRemoteToolsExternal = parsedExternalMcp?.enabled_remote_tools
      const normalizedEnabledRemoteTools = (() => {
        const source =
          Array.isArray(parsedEnabledRemoteToolsRoot)
            ? parsedEnabledRemoteToolsRoot
            : (Array.isArray(parsedEnabledRemoteToolsExternal) ? parsedEnabledRemoteToolsExternal : null)
        if (!source) return null
        return source
          .filter((item): item is string => typeof item === 'string')
          .map((item) => item.trim())
          .filter(Boolean)
      })()

      const parsedRemoteCatalogRaw = parsedExternalMcp?.remote_tools_catalog
      if (Array.isArray(parsedRemoteCatalogRaw)) {
        const normalizedCatalog = parsedRemoteCatalogRaw
          .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
          .map((item) => ({
            remote_tool_name: String(item.remote_tool_name || '').trim(),
            display_name: String(item.display_name || item.remote_tool_name || '').trim(),
            description: typeof item.description === 'string' ? item.description : '',
            input_schema: item.input_schema && typeof item.input_schema === 'object'
              ? (item.input_schema as Record<string, unknown>)
              : {},
          }))
          .filter((item) => item.remote_tool_name.length > 0)
        if (normalizedCatalog.length > 0) {
          setMcpDiscoveredTools(normalizedCatalog)
        }
      } else if (Array.isArray(normalizedEnabledRemoteTools) && normalizedEnabledRemoteTools.length > 0) {
        setMcpDiscoveredTools(
          normalizedEnabledRemoteTools.map((remoteToolName) => ({
            remote_tool_name: remoteToolName,
            display_name: remoteToolName,
            description: '',
            input_schema: {},
          }))
        )
      }

      const parsedMcpTransportRaw =
        (normalizedParsed as { mcp_transport?: unknown }).mcp_transport
        ?? parsedExternalMcp?.transport
      const parsedMcpAuthTypeRaw =
        (normalizedParsed as { mcp_auth_type?: unknown }).mcp_auth_type
        ?? parsedExternalMcp?.auth_type
      const parsedMcpHeaderNameRaw =
        (normalizedParsed as { mcp_auth_header_name?: unknown }).mcp_auth_header_name
        ?? parsedExternalMcp?.auth_header_name
      const parsedMcpSecretIdRaw =
        (normalizedParsed as { mcp_auth_secret_id?: unknown }).mcp_auth_secret_id
        ?? parsedExternalMcp?.auth_secret_id

      const nextState: ToolFormState = {
        ...formData,
        ...(normalizedParsed as Partial<ToolFormState>),
        execution_type:
          normalizedParsed.execution_type === 'webhook' || normalizedParsed.execution_type === 'mcp_server'
            ? normalizedParsed.execution_type
            : formData.execution_type,
        http_method: parsedHttpMethod ?? formData.http_method,
        http_url: parsedHttpUrl ?? formData.http_url,
        request_headers:
          parsedRequestHeaders !== undefined
            ? normalizeHeaderDrafts({
                method: parsedHttpMethod ?? formData.http_method,
                url: parsedHttpUrl ?? formData.http_url,
                request_headers: Array.isArray(parsedRequestHeaders)
                  ? (parsedRequestHeaders as NonNullable<MCPAction['http_config']>['request_headers'])
                  : [],
              })
            : formData.request_headers,
        path_params_schema:
          parsedPathParams !== undefined || parsedQueryParams !== undefined || parsedRequestBodyParams !== undefined
            ? parsedStudioDrafts.path_params_schema
            : formData.path_params_schema,
        query_params_schema:
          parsedPathParams !== undefined || parsedQueryParams !== undefined || parsedRequestBodyParams !== undefined
            ? parsedStudioDrafts.query_params_schema
            : formData.query_params_schema,
        mcp_url: parsedMcpUrl ?? formData.mcp_url,
        mcp_enabled_remote_tools: normalizedEnabledRemoteTools ?? formData.mcp_enabled_remote_tools,
        mcp_transport:
          parsedMcpTransportRaw === 'http_jsonrpc' || parsedMcpTransportRaw === 'streamable_http'
            ? parsedMcpTransportRaw
            : formData.mcp_transport,
        mcp_auth_type:
          parsedMcpAuthTypeRaw === 'none' || parsedMcpAuthTypeRaw === 'bearer' || parsedMcpAuthTypeRaw === 'header'
            ? parsedMcpAuthTypeRaw
            : formData.mcp_auth_type,
        mcp_auth_header_name:
          typeof parsedMcpHeaderNameRaw === 'string'
            ? parsedMcpHeaderNameRaw
            : formData.mcp_auth_header_name,
        mcp_auth_secret_id:
          typeof parsedMcpSecretIdRaw === 'string'
            ? parsedMcpSecretIdRaw
            : formData.mcp_auth_secret_id,
        classification: normalizeStudioVisibility(normalizedParsed.classification as ToolVisibility | undefined),
      }
      setFormData(nextState)
      setJsonError(null)
      return nextState
    } catch {
      if (!suppressErrors) setJsonError('El JSON no es válido.')
      return null
    }
  }

  const updateField = <K extends keyof ToolFormState>(field: K, value: ToolFormState[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const syncUrlAndPathParams = React.useCallback((nextUrl: string) => {
    setFormData((prev) => {
      return {
        ...prev,
        http_url: nextUrl,
        path_params_schema: reconcilePathParamsWithUrl(nextUrl, prev.path_params_schema),
      }
    })
  }, [])

  const addHeader = () => {
    updateField('request_headers', [...formData.request_headers, createHeaderDraft()])
  }

  const removeHeader = (id: string) => {
    updateField('request_headers', formData.request_headers.filter((header) => header.id !== id))
  }

  const updateHeader = (id: string, patch: Partial<HeaderDraft>) => {
    updateField(
      'request_headers',
      formData.request_headers.map((header) => (header.id === id ? { ...header, ...patch } : header))
    )
  }

  const addParam = (kind: 'path_params_schema' | 'query_params_schema') => {
    setFormData((prev) => ({
      ...prev,
      [kind]: [...prev[kind], createParamDraft()],
    }))
  }

  const updateParam = (kind: 'path_params_schema' | 'query_params_schema', paramIndex: number, patch: Partial<ParamDraft>) => {
    setFormData((prev) => {
      const currentParam = prev[kind][paramIndex]
      const previousId = currentParam?.id?.trim() || ''
      const nextParams = prev[kind].map((param, index) =>
        index === paramIndex
          ? (() => {
              const nextType = patch.type ?? param.type
              const nextValueType = patch.value_type ?? param.value_type
              const nextValue = nextValueType === 'llm_prompt' ? '' : (patch.value ?? param.value)
              const nextEnum =
                (nextType === 'string' || nextType === 'number' || nextType === 'integer') &&
                nextValueType === 'llm_prompt'
                  ? sanitizeEnumValues(patch.enum ?? param.enum)
                  : []
              return {
                ...param,
                ...patch,
                value: nextValue,
                enum: nextEnum,
                draft_id: param.draft_id || `param_draft_${kind}_${index}`,
              }
            })()
          : param
      )
      if (kind !== 'path_params_schema') {
        return {
          ...prev,
          [kind]: nextParams,
        }
      }
      const nextId = (nextParams[paramIndex]?.id || '').trim()
      let nextUrl = prev.http_url
      if (previousId && nextId && previousId !== nextId) {
        nextUrl = renamePathPlaceholderInUrl(nextUrl, previousId, nextId)
      } else if (!previousId && nextId) {
        nextUrl = ensurePathPlaceholderInUrl(nextUrl, nextId)
      } else if (previousId && !nextId) {
        nextUrl = removePathPlaceholderFromUrl(nextUrl, previousId)
      }
      return {
        ...prev,
        http_url: nextUrl,
        [kind]: nextParams,
      }
    })
  }

  const removeParam = (kind: 'path_params_schema' | 'query_params_schema', paramIndex: number) => {
    const target = formData[kind][paramIndex]
    if (target) {
      const paramKey = buildParamStableKey(kind, target, paramIndex)
      setParamEnumInputByKey((prev) => {
        if (!(paramKey in prev)) return prev
        const next = { ...prev }
        delete next[paramKey]
        return next
      })
    }
    setFormData((prev) => ({
      ...prev,
      http_url: kind === 'path_params_schema' ? removePathPlaceholderFromUrl(prev.http_url, target?.id || '') : prev.http_url,
      [kind]: prev[kind].filter((_, index) => index !== paramIndex),
    }))
  }

  const setParamEnumInput = (paramKey: string, value: string) => {
    setParamEnumInputByKey((prev) => ({ ...prev, [paramKey]: value }))
  }

  const addParamEnumValue = (kind: 'path_params_schema' | 'query_params_schema', param: ParamDraft, paramIndex: number) => {
    const paramKey = buildParamStableKey(kind, param, paramIndex)
    const draftValue = (paramEnumInputByKey[paramKey] || '').trim()
    if (!draftValue) return
    const nextEnum = sanitizeEnumValues([...(param.enum || []), draftValue])
    updateParam(kind, paramIndex, { enum: nextEnum })
    setParamEnumInput(paramKey, '')
  }

  const renderParamEnumEditor = (
    kind: 'path_params_schema' | 'query_params_schema',
    param: ParamDraft,
    paramIndex: number
  ) => {
    if (
      (param.type !== 'string' && param.type !== 'number' && param.type !== 'integer') ||
      param.value_type !== 'llm_prompt'
    ) {
      return null
    }
    const paramKey = buildParamStableKey(kind, param, paramIndex)
    const enumInputValue = paramEnumInputByKey[paramKey] || ''
    const normalizedCandidate = enumInputValue.trim()
    const canAddEnum = normalizedCandidate.length > 0 && !param.enum.includes(normalizedCandidate)

    return (
      <div className="space-y-2">
        <Label className="text-xs font-medium">Enum Values (optional)</Label>
        <div className="flex items-center gap-2">
          <Input
            value={enumInputValue}
            onChange={(event) => setParamEnumInput(paramKey, event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return
              event.preventDefault()
              if (!canAddEnum) return
              addParamEnumValue(kind, param, paramIndex)
            }}
            placeholder="Introduce un valor enum"
            disabled={loading}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => addParamEnumValue(kind, param, paramIndex)}
            disabled={loading || !canAddEnum}
            aria-label="Añadir valor enum"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Añade valores predefinidos que el LLM pueda seleccionar. Si no se proporcionan valores, el LLM puede usar cualquier valor de texto.
        </p>
        {param.enum.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {param.enum.map((enumValue, enumIndex) => (
              <TagBadge
                key={`${paramKey}-enum-${enumValue}-${enumIndex}`}
                id={`${enumIndex}`}
                name={enumValue}
                variant="secondary"
                size="sm"
                removable
                onRemove={() => updateParam(kind, paramIndex, { enum: param.enum.filter((_, index) => index !== enumIndex) })}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage(null)

    const workingFormData = editorMode === 'json' ? applyJsonDraft() : formData
    if (!workingFormData) return

    if (!workingFormData.display_name.trim()) { setErrorMessage('El nombre visible es obligatorio.'); return }
    if (workingFormData.execution_type === 'mcp_server' && slugifyToolName(workingFormData.display_name).length === 0) {
      setErrorMessage('El nombre visible debe incluir letras o números para generar el nombre interno.')
      return
    }
    if (workingFormData.execution_type !== 'mcp_server' && !workingFormData.name.trim()) { setErrorMessage('El nombre interno es obligatorio.'); return }
    if (workingFormData.execution_type === 'mcp_server' && workingFormData.mcp_enabled_remote_tools.length === 0) {
      setErrorMessage('Debes habilitar al menos una herramienta remota para MCP Server.')
      return
    }
    if (workingFormData.execution_type === 'mcp_server' && workingFormData.mcp_auth_type !== 'none' && !workingFormData.mcp_auth_secret_id.trim()) {
      setErrorMessage('La API key/token es obligatoria para este tipo de autenticación.')
      return
    }
    if (workingFormData.execution_type === 'webhook') {
      if (!isValidHttpUrl(workingFormData.http_url)) {
        setErrorMessage('La URL debe ser absoluta y comenzar con http:// o https://.')
        return
      }
      const headersError = getWebhookHeaderValidationError(workingFormData.request_headers)
      if (headersError) {
        setErrorMessage(headersError)
        return
      }
      const pathSchemaError = getWebhookParamValidationError(workingFormData.path_params_schema, 'ruta')
      if (pathSchemaError) {
        setErrorMessage(pathSchemaError)
        return
      }
      const transportSchemaError = getWebhookParamValidationError(
        workingFormData.query_params_schema,
        methodUsesQueryParams(workingFormData.http_method) ? 'consulta' : 'cuerpo'
      )
      if (transportSchemaError) {
        setErrorMessage(transportSchemaError)
        return
      }
    }

    setLoading(true)
    const data: MCPActionCreate = {
      name: workingFormData.execution_type === 'mcp_server'
        ? slugifyToolName(workingFormData.display_name)
        : workingFormData.name.trim(),
      display_name: workingFormData.display_name.trim(),
      description: workingFormData.description.trim(),
      execution_type: workingFormData.execution_type,
      is_enabled: workingFormData.is_enabled,
      timeout_seconds: workingFormData.timeout_seconds,
      retry_count: workingFormData.retry_count,
      classification: workingFormData.classification,
    }

    if (workingFormData.execution_type === 'webhook' && workingFormData.http_url) {
      const schemaPayload = buildWebhookSchemasFromStudioDrafts(
        workingFormData.http_method,
        workingFormData.path_params_schema,
        workingFormData.query_params_schema
      )
      data.http_config = {
        method: workingFormData.http_method,
        url: workingFormData.http_url,
        request_headers: toRequestHeadersPayload(workingFormData.request_headers),
        ...schemaPayload,
      }
    }
    if (workingFormData.execution_type === 'mcp_server' && workingFormData.mcp_url) {
      data.http_config = {
        method: 'POST',
        url: workingFormData.mcp_url,
        external_mcp: buildExternalMcpConfig({
          mcp_url: workingFormData.mcp_url,
          mcp_transport: workingFormData.mcp_transport,
          mcp_auth_type: workingFormData.mcp_auth_type,
          mcp_auth_header_name: workingFormData.mcp_auth_header_name,
          mcp_auth_secret_id: workingFormData.mcp_auth_secret_id,
          discovered_tools: mcpDiscoveredTools,
          enabled_remote_tools: workingFormData.mcp_enabled_remote_tools,
        }),
      }
    }
    try {
      let savedTool: MCPAction | null = null
      if (tool) {
        savedTool = await toolsService.updateAction(tool.id, data)
      } else {
        savedTool = await toolsService.createAction(data)
      }
      if (savedTool?.id) {
        saveToolIcon(savedTool.id, {
          icon: selectedIcon,
          colorClass: iconColorClass || undefined,
        })
      }
      onSuccess?.()
      onOpenChange(false)
    } catch (error) {
      console.error('Error saving tool:', error)
      setErrorMessage(toErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent side="right" className="border-border p-0 sm:w-[70vw] lg:w-[66vw] xl:w-[62vw] 2xl:w-[58vw] sm:max-w-none sm:rounded-none">
        <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col">
          <DialogHeader className="border-b border-border bg-card/70 px-4 py-3 sm:px-6 sm:py-4">
            <div className="flex items-center justify-between gap-2 pr-8 sm:gap-2 sm:pr-10">
              <DialogTitle className="text-xl font-semibold sm:text-2xl">
                {tool ? 'Editar herramienta' : 'Crear herramienta'}
              </DialogTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mr-2"
                onClick={() => {
                  if (editorMode === 'form') {
                    setJsonDraft(buildJsonSnapshot())
                    setJsonError(null)
                    setEditorMode('json')
                    return
                  }
                  applyJsonDraft({ suppressErrors: true })
                  setJsonError(null)
                  setEditorMode('form')
                }}
                disabled={loading}
              >
                {isMobile ? (editorMode === 'form' ? 'JSON' : 'Form') : (editorMode === 'form' ? 'Editar JSON' : 'Editar formulario')}
              </Button>
            </div>
          </DialogHeader>

          {errorMessage && (
            <div className="px-6 pt-4">
              <Alert variant="destructive">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-hidden">
            <ScrollArea className="h-full overflow-x-hidden">
              {editorMode === 'json' ? (
                <div className="space-y-2 p-6">
                  <Label htmlFor="tool-json-editor">Edición JSON de la herramienta</Label>
                  <Textarea
                    id="tool-json-editor"
                    value={jsonDraft}
                    onChange={(e) => {
                      setJsonDraft(e.target.value)
                      if (jsonError) setJsonError(null)
                    }}
                    className="min-h-[560px] font-mono text-xs"
                    placeholder='{"name":"tool","execution_type":"webhook"}'
                    disabled={loading}
                  />
                  {jsonError && <p className="text-sm text-destructive">{jsonError}</p>}
                </div>
              ) : (
              <div className="grid min-w-0 gap-4 p-4 sm:p-6">
                <section className="min-w-0 space-y-4">
                  <div className="rounded-lg border bg-card/50 p-4">
                    <div className="mb-4 rounded-lg border bg-background/50 p-3">
                      <Label className="text-xs">Icono</Label>
                      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className="mt-2 w-full justify-start gap-2"
                            disabled={loading}
                          >
                            {renderVisualIcon(selectedIcon, iconColorClass, 'h-4 w-4 text-base')}
                            <span className="text-sm">Elegir icono</span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          portalled={!isMobile}
                          className={cn(
                            "p-3 max-h-[75dvh] overflow-auto overscroll-contain",
                            isMobile ? "w-[min(360px,calc(100vw-2rem))]" : "w-[360px]"
                          )}
                          align="start"
                          sideOffset={8}
                          style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
                        >
                          {/* El mismo selector que Agent Studio y los filtros de
                              Conversations. Esta era la CUARTA copia, con su
                              propia lista de emoji, su propia rejilla y una
                              cadena de siete comparaciones para pintar la
                              muestra de color. */}
                          <SelectorVisual
                            visual={{ icon: selectedIcon, colorClass: iconColorClass || undefined }}
                            iconos={ICON_OPTIONS}
                            columnasIconos={10}
                            columnasEmojis={8}
                            renderIcono={(id, color) => renderVisualIcon(id, color, 'h-4 w-4')}
                            onElegido={() => setPickerOpen(false)}
                            onVisualChange={(v) => {
                              setIconColorClass(v.colorClass || '')
                              if (v.icon) setSelectedIcon(v.icon)
                            }}
                          />
                          {selectedIcon && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="mt-1 w-full text-muted-foreground"
                              onClick={() => {
                                setSelectedIcon('emoji:🔧')
                                setIconColorClass('')
                              }}
                              disabled={loading}
                            >
                              Quitar icono
                            </Button>
                          )}
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="edit-tool-display-name">Nombre visible *</Label>
                        <Input
                          id="edit-tool-display-name"
                          value={formData.display_name}
                          onChange={(e) => {
                            const value = e.target.value
                            if (formData.execution_type === 'mcp_server') {
                              setFormData((prev) => ({
                                ...prev,
                                display_name: value,
                                name: slugifyToolName(value),
                              }))
                              return
                            }
                            updateField('display_name', value)
                          }}
                          placeholder="Buscar Propiedades"
                          disabled={loading}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-tool-name">Nombre interno *</Label>
                        {formData.execution_type === 'mcp_server' ? (
                          <Input
                            id="edit-tool-name"
                            value={slugifyToolName(formData.display_name)}
                            placeholder="search_properties"
                            className="font-mono"
                            disabled
                            readOnly
                          />
                        ) : (
                          <Input
                            id="edit-tool-name"
                            value={formData.name}
                            onChange={(e) => updateField('name', e.target.value)}
                            placeholder="search_properties"
                            className="font-mono"
                            disabled={loading}
                          />
                        )}
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      <Label>Visibilidad de herramienta</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {([
                          { value: 'external', label: 'Público' },
                          { value: 'internal', label: 'Privado' },
                        ] as const).map((option) => {
                          const active = formData.classification === option.value
                          return (
                            <Button
                              key={option.value}
                              type="button"
                              variant={active ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => updateField('classification', option.value)}
                              disabled={loading}
                            >
                              {option.label}
                            </Button>
                          )
                        })}
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      <Label htmlFor="edit-tool-description">Descripción</Label>
                      <Textarea
                        id="edit-tool-description"
                        value={formData.description}
                        onChange={(e) => updateField('description', e.target.value)}
                        placeholder="Qué hace esta herramienta..."
                        rows={3}
                        disabled={loading}
                      />
                    </div>

                    <div className="mt-4">
                      <div className="flex items-center justify-between rounded-lg border bg-background/60 px-4 py-3">
                        <div>
                          <p className="text-sm font-medium">Estado</p>
                          <p className={cn('text-xs font-medium', formData.is_enabled ? 'text-success' : 'text-warning')}>
                            {formData.is_enabled ? 'Habilitado' : 'Deshabilitado'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={cn(formData.is_enabled ? 'border-success/30 text-success' : 'border-warning/30 text-warning')}
                            onClick={() => updateField('is_enabled', !formData.is_enabled)}
                            disabled={loading}
                          >
                            {formData.is_enabled ? 'ON' : 'OFF'}
                          </Button>
                          <Switch
                            checked={formData.is_enabled}
                            onCheckedChange={(checked) => updateField('is_enabled', checked)}
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border bg-card/50 p-4 space-y-4">
                    <div className="space-y-2">
                      <Label>Tipo de ejecución</Label>
                      <Select
                        value={formData.execution_type}
                        onValueChange={(v: 'webhook' | 'mcp_server') => updateField('execution_type', v)}
                        disabled={loading || Boolean(tool)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="webhook">Webhook / HTTP Request</SelectItem>
                          <SelectItem value="mcp_server">MCP Server</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.execution_type === 'webhook' && (
                      <>
                        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[160px_minmax(0,1fr)]">
                          <div className="space-y-2">
                            <Label>Método HTTP</Label>
                            <Select
                              value={formData.http_method}
                              onValueChange={(v: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE') =>
                                updateField('http_method', v)
                              }
                              disabled={loading}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="GET">GET</SelectItem>
                                <SelectItem value="POST">POST</SelectItem>
                                <SelectItem value="PUT">PUT</SelectItem>
                                <SelectItem value="PATCH">PATCH</SelectItem>
                                <SelectItem value="DELETE">DELETE</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit-tool-url">URL *</Label>
                            <Input
                              id="edit-tool-url"
                              value={formData.http_url}
                              onChange={(e) => syncUrlAndPathParams(e.target.value)}
                              placeholder="https://api.example.com/endpoint"
                              disabled={loading}
                              required
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label>Headers</Label>
                            <Button type="button" variant="outline" size="sm" onClick={addHeader} disabled={loading}>
                              <Plus className="mr-1 h-4 w-4" />
                              Añadir
                            </Button>
                          </div>
                          {formData.request_headers.map((header) => (
                            <div key={header.id} className="grid min-w-0 grid-cols-1 gap-2 xl:grid-cols-[minmax(0,1.1fr)_180px_minmax(0,1.1fr)_auto]">
                              <Input
                                placeholder="Header"
                                value={header.name}
                                onChange={(e) => updateHeader(header.id, { name: e.target.value })}
                                disabled={loading}
                              />
                              <Select
                                value={header.value_type}
                                onValueChange={(value: HeaderDraft['value_type']) => updateHeader(header.id, { value_type: value })}
                                disabled={loading}
                              >
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="valor">Valor</SelectItem>
                                  <SelectItem value="variable_dinamica">Variable dinamica</SelectItem>
                                  <SelectItem value="secreto">Secreto</SelectItem>
                                </SelectContent>
                              </Select>
                              {header.value_type === 'secreto' ? (
                                <SecretSelectField value={header.value} onChange={(secretId) => updateHeader(header.id, { value: secretId })} disabled={loading} />
                              ) : (
                                <Input
                                  placeholder={header.value_type === 'variable_dinamica' ? 'Variable' : 'Valor'}
                                  value={header.value}
                                  onChange={(e) => updateHeader(header.id, { value: e.target.value })}
                                  disabled={loading}
                                />
                              )}
                              <Button type="button" variant="ghost" size="sm" onClick={() => removeHeader(header.id)} disabled={loading}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>

                        <div className="space-y-2 rounded-lg border bg-background/50 p-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                              JSON (schema)
                            </Label>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setShowSchemaJson((prev) => !prev)}
                                disabled={loading}
                              >
                                {showSchemaJson ? 'Ocultar JSON' : 'Ver JSON'}
                              </Button>
                              {showSchemaJson && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={handleCopySchema}
                                  disabled={loading}
                                >
                                  <Copy className="mr-1 h-4 w-4" />
                                  {schemaCopied ? 'Copiado' : 'Copiar'}
                                </Button>
                              )}
                            </div>
                          </div>
                          {showSchemaJson && (
                            <Textarea
                              readOnly
                              value={buildSchemaExportSnapshot()}
                              className="min-h-[180px] font-mono text-xs"
                            />
                          )}
                        </div>
                      </>
                    )}

                    {formData.execution_type === 'mcp_server' && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="edit-tool-mcp-url">URL del servidor MCP *</Label>
                          <Input
                            id="edit-tool-mcp-url"
                            value={formData.mcp_url}
                            onChange={(e) => updateField('mcp_url', e.target.value)}
                            placeholder="https://mcp.example.com/rpc"
                            disabled={loading}
                            required
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                          <div className="space-y-2">
                            <Label>Auth Type</Label>
                            <Select
                              value={formData.mcp_auth_type}
                              onValueChange={(v: 'none' | 'bearer' | 'header') => {
                                if (v === 'none') {
                                  setFormData((prev) => ({ ...prev, mcp_auth_type: v, mcp_auth_secret_id: '' }))
                                  return
                                }
                                updateField('mcp_auth_type', v)
                              }}
                              disabled={loading}
                            >
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">none</SelectItem>
                                <SelectItem value="bearer">bearer</SelectItem>
                                <SelectItem value="header">header</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        {formData.mcp_auth_type !== 'none' && (
                          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                            <div className="space-y-2">
                              <Label>API key / token</Label>
                              <SecretSelectField
                                value={formData.mcp_auth_secret_id}
                                onChange={(secretId) => updateField('mcp_auth_secret_id', secretId)}
                                disabled={loading}
                              />
                            </div>
                          </div>
                        )}
                        <div className="rounded-lg border bg-background/50 p-3 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium">Herramientas del servidor MCP</p>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={discoverMcpTools}
                              disabled={loading || mcpDiscovering}
                            >
                              {mcpDiscovering ? 'Explorando...' : 'Explorar herramientas'}
                            </Button>
                          </div>
                          {mcpExploreError && (
                            <p className="text-xs text-destructive">{mcpExploreError}</p>
                          )}
                          {mcpDiscoveredTools.length > 0 && (
                            <div className="max-h-56 space-y-2 overflow-auto pr-1">
                              {mcpDiscoveredTools.map((item) => {
                                const selected = formData.mcp_enabled_remote_tools.includes(item.remote_tool_name)
                                return (
                                  <div
                                    key={item.remote_tool_name}
                                    className={cn(
                                      'flex items-center justify-between gap-2 rounded-lg border p-2.5',
                                      selected ? 'border-primary/60 bg-primary/5' : 'border-border'
                                    )}
                                  >
                                    <p className="truncate text-sm font-medium">{item.display_name || item.remote_tool_name}</p>
                                    <div className="flex shrink-0 items-center gap-2">
                                      <Button type="button" size="sm" variant="outline" onClick={() => setMcpToolDetail(item)} disabled={loading}>
                                        Ver detalle
                                      </Button>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant={selected ? 'default' : 'secondary'}
                                        onClick={() => toggleMcpRemoteTool(item.remote_tool_name, !selected)}
                                        disabled={loading}
                                      >
                                        {selected ? 'Activa' : 'Activar'}
                                      </Button>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {formData.execution_type === 'webhook' && (
                      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="edit-tool-timeout">Timeout (s)</Label>
                          <Input
                            id="edit-tool-timeout"
                            type="number"
                            value={formData.timeout_seconds}
                            onChange={(e) => updateField('timeout_seconds', parseInt(e.target.value) || 30)}
                            disabled={loading}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-tool-retry">Reintentos</Label>
                          <Input
                            id="edit-tool-retry"
                            type="number"
                            value={formData.retry_count}
                            onChange={(e) => updateField('retry_count', parseInt(e.target.value) || 0)}
                            disabled={loading}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                {formData.execution_type === 'webhook' && (
                <section className="min-w-0 space-y-4">
                  <div className="min-w-0 rounded-lg border bg-card/50 p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Parámetros de ruta</Label>
                      <Button type="button" variant="outline" size="sm" onClick={() => addParam('path_params_schema')} disabled={loading}>
                        <Plus className="mr-1 h-4 w-4" />
                        Añadir
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Estos parámetros se insertan en la URL como {"{param}"} o ":param". Ejemplo: si defines "id", usa /recurso/{"{id}"}.
                    </p>
                    {formData.path_params_schema.length > 0 && (
                    <div className="hidden items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground xl:grid xl:grid-cols-[minmax(0,1.4fr)_130px_160px_auto]">
                      <span>Clave (ID)</span>
                      <span>Tipo</span>
                      <span>Origen valor</span>
                      <span />
                    </div>
                    )}
                    {formData.path_params_schema.map((param, paramIndex) => {
                      const rowKey = param.draft_id || `toolform-path-param-${paramIndex}`
                      const idInputRefKey = buildParamInputRefKey('path_params_schema', param, paramIndex, 'id')
                      const descriptionInputRefKey = buildParamInputRefKey('path_params_schema', param, paramIndex, 'description')
                      const valueInputRefKey = buildParamInputRefKey('path_params_schema', param, paramIndex, 'value')
                      return (
                      <div key={rowKey} className="min-w-0 space-y-2 rounded-lg border p-3">
                        <div className="grid min-w-0 grid-cols-1 gap-2 xl:grid-cols-[minmax(0,1.4fr)_130px_160px_auto]">
                          <Input
                            ref={(node) => setParamInputNode(idInputRefKey, node)}
                            value={param.id}
                            onChange={(e) => {
                              rememberParamInputFocus(idInputRefKey, e.currentTarget)
                              updateParam('path_params_schema', paramIndex, { id: e.target.value })
                            }}
                            placeholder="item_id"
                            aria-label="Clave del parámetro de ruta"
                            disabled={loading}
                          />
                          <Select value={param.type} onValueChange={(value: ParamDraft['type']) => updateParam('path_params_schema', paramIndex, { type: value })} disabled={loading}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="string">String</SelectItem>
                                <SelectItem value="boolean">Boolean</SelectItem>
                                <SelectItem value="integer">Integer</SelectItem>
                                <SelectItem value="number">Number</SelectItem>
                              </SelectContent>
                            </Select>
                          <Select value={param.value_type} onValueChange={(value: ParamDraft['value_type']) => updateParam('path_params_schema', paramIndex, { value_type: value })} disabled={loading}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="llm_prompt">LLM Prompt</SelectItem>
                              <SelectItem value="constant">Constante</SelectItem>
                              <SelectItem value="dynamic_variable">Variable</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeParam('path_params_schema', paramIndex)} disabled={loading}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 gap-2 xl:grid-cols-[minmax(0,1fr)_220px]">
                          <Input
                            ref={(node) => setParamInputNode(descriptionInputRefKey, node)}
                            value={param.description}
                            onChange={(e) => {
                              rememberParamInputFocus(descriptionInputRefKey, e.currentTarget)
                              updateParam('path_params_schema', paramIndex, { description: e.target.value })
                            }}
                            placeholder="Descripción"
                            disabled={loading}
                          />
                          {param.value_type !== 'llm_prompt' && (
                            <Input
                              ref={(node) => setParamInputNode(valueInputRefKey, node)}
                              value={param.value}
                              onChange={(e) => {
                                rememberParamInputFocus(valueInputRefKey, e.currentTarget)
                                updateParam('path_params_schema', paramIndex, { value: e.target.value })
                              }}
                              placeholder={param.value_type === 'constant' ? 'Valor constante' : 'Variable'}
                              disabled={loading}
                            />
                          )}
                        </div>
                        {renderParamEnumEditor('path_params_schema', param, paramIndex)}
                        <div className="inline-flex items-center gap-2 text-xs">
                          <Switch
                            checked={param.required}
                            onCheckedChange={(checked) => updateParam('path_params_schema', paramIndex, { required: checked })}
                            disabled={loading}
                          />
                          <span className={cn('font-medium', param.required ? 'text-success' : 'text-muted-foreground')}>
                            Requerido
                          </span>
                          <span
                            className={cn(
                              'inline-flex h-5 items-center rounded-md border px-1.5 text-xs uppercase tracking-wide',
                              param.required ? 'border-success/30 text-success' : 'border-border text-muted-foreground'
                            )}
                          >
                            {param.required ? 'Si' : 'No'}
                          </span>
                        </div>
                      </div>
                    )})}
                  </div>

                  <div className="min-w-0 rounded-lg border bg-card/50 p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Parámetros de consulta</Label>
                      <Button type="button" variant="outline" size="sm" onClick={() => addParam('query_params_schema')} disabled={loading}>
                        <Plus className="mr-1 h-4 w-4" />
                        Añadir
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    GET y DELETE envían estos parámetros en query string. POST, PUT y PATCH los envían en el body.
                  </p>
                    {formData.query_params_schema.length > 0 && (
                    <div className="hidden items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground xl:grid xl:grid-cols-[minmax(0,1.4fr)_130px_160px_auto]">
                      <span>Clave (query key)</span>
                      <span>Tipo</span>
                      <span>Origen valor</span>
                      <span />
                    </div>
                    )}
                    {formData.query_params_schema.map((param, paramIndex) => {
                      const rowKey = param.draft_id || `toolform-query-param-${paramIndex}`
                      const idInputRefKey = buildParamInputRefKey('query_params_schema', param, paramIndex, 'id')
                      const descriptionInputRefKey = buildParamInputRefKey('query_params_schema', param, paramIndex, 'description')
                      const valueInputRefKey = buildParamInputRefKey('query_params_schema', param, paramIndex, 'value')
                      return (
                      <div key={rowKey} className="min-w-0 space-y-2 rounded-lg border p-3">
                        <div className="grid min-w-0 grid-cols-1 gap-2 xl:grid-cols-[minmax(0,1.4fr)_130px_160px_auto]">
                          <Input
                            ref={(node) => setParamInputNode(idInputRefKey, node)}
                            value={param.id}
                            onChange={(e) => {
                              rememberParamInputFocus(idInputRefKey, e.currentTarget)
                              updateParam('query_params_schema', paramIndex, { id: e.target.value })
                            }}
                            placeholder="city"
                            aria-label="Clave del parámetro de consulta"
                            disabled={loading}
                          />
                          <Select value={param.type} onValueChange={(value: ParamDraft['type']) => updateParam('query_params_schema', paramIndex, { type: value })} disabled={loading}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="string">String</SelectItem>
                                <SelectItem value="boolean">Boolean</SelectItem>
                                <SelectItem value="integer">Integer</SelectItem>
                                <SelectItem value="number">Number</SelectItem>
                              </SelectContent>
                            </Select>
                          <Select value={param.value_type} onValueChange={(value: ParamDraft['value_type']) => updateParam('query_params_schema', paramIndex, { value_type: value })} disabled={loading}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="llm_prompt">LLM Prompt</SelectItem>
                              <SelectItem value="constant">Constante</SelectItem>
                              <SelectItem value="dynamic_variable">Variable</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeParam('query_params_schema', paramIndex)} disabled={loading}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 gap-2 xl:grid-cols-[minmax(0,1fr)_220px]">
                          <Input
                            ref={(node) => setParamInputNode(descriptionInputRefKey, node)}
                            value={param.description}
                            onChange={(e) => {
                              rememberParamInputFocus(descriptionInputRefKey, e.currentTarget)
                              updateParam('query_params_schema', paramIndex, { description: e.target.value })
                            }}
                            placeholder="Descripción"
                            disabled={loading}
                          />
                          {param.value_type !== 'llm_prompt' && (
                            <Input
                              ref={(node) => setParamInputNode(valueInputRefKey, node)}
                              value={param.value}
                              onChange={(e) => {
                                rememberParamInputFocus(valueInputRefKey, e.currentTarget)
                                updateParam('query_params_schema', paramIndex, { value: e.target.value })
                              }}
                              placeholder={param.value_type === 'constant' ? 'Valor constante' : 'Variable'}
                              disabled={loading}
                            />
                          )}
                        </div>
                        {renderParamEnumEditor('query_params_schema', param, paramIndex)}
                        <div className="inline-flex items-center gap-2 text-xs">
                          <Switch
                            checked={param.required}
                            onCheckedChange={(checked) => updateParam('query_params_schema', paramIndex, { required: checked })}
                            disabled={loading}
                          />
                          <span className={cn('font-medium', param.required ? 'text-success' : 'text-muted-foreground')}>
                            Requerido
                          </span>
                          <span
                            className={cn(
                              'inline-flex h-5 items-center rounded-md border px-1.5 text-xs uppercase tracking-wide',
                              param.required ? 'border-success/30 text-success' : 'border-border text-muted-foreground'
                            )}
                          >
                            {param.required ? 'Si' : 'No'}
                          </span>
                        </div>
                      </div>
                    )})}
                  </div>
                </section>
                )}
              </div>
              )}
            </ScrollArea>
          </div>

          <Dialog open={Boolean(mcpToolDetail)} onOpenChange={(nextOpen) => { if (!nextOpen) setMcpToolDetail(null) }}>
            <DialogContent className="max-h-[85vh] max-w-2xl overflow-hidden">
              <DialogHeader>
                <DialogTitle>{mcpToolDetail?.display_name || mcpToolDetail?.remote_tool_name || 'Detalle de herramienta'}</DialogTitle>
                <DialogDescription className="font-mono text-xs">
                  {mcpToolDetail?.remote_tool_name}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 overflow-auto pr-1">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Descripción</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                    {mcpToolDetail?.description || 'Sin descripción'}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Input schema</p>
                  <pre className="mt-1 max-h-[40vh] overflow-auto rounded-md border bg-muted/30 p-3 text-xs">
                    {JSON.stringify(mcpToolDetail?.input_schema || {}, null, 2)}
                  </pre>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <DialogFooter className="sticky bottom-0 z-20 flex-row min-w-0 items-center gap-2 border-t border-border bg-background px-3 py-2 sm:px-6 sm:py-4">
            <Button variant="outline" size="sm" type="button" className="shrink-0 whitespace-nowrap" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" size="sm" className="ml-auto shrink-0 whitespace-nowrap" disabled={loading}>
              {loading ? 'Guardando...' : tool ? 'Guardar cambios' : 'Crear herramienta'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
